import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  cn,
} from "@jem-open/jem-ui";
import { Check, ChevronDown } from "lucide-react";
import { useUniqueId } from "../utils/useUniqueId";

export interface SearchableSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  /** Used for search matching. Defaults to label + sublabel if not set. */
  keywords?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  emptyMessage: string;
  className?: string;
}

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  className,
}: SearchableSelectProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listboxId = useUniqueId();
  const optionIdBase = useUniqueId();
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = (
        o.keywords ?? `${o.label} ${o.sublabel ?? ""}`
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    setHighlightedIndex((prev) => {
      if (filtered.length === 0) return 0;
      return Math.max(0, Math.min(prev, filtered.length - 1));
    });
  }, [query, open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[highlightedIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, open]);

  const closePopover = (): void => {
    setOpen(false);
    setQuery("");
  };

  const commitSelection = (index: number): void => {
    const opt = filtered[index];
    if (!opt) return;
    onChange(opt.value);
    closePopover();
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Escape") {
      closePopover();
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlightedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlightedIndex(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitSelection(highlightedIndex);
    }
  };

  const activeOptionId =
    filtered.length > 0 && open
      ? `${optionIdBase}-${highlightedIndex}`
      : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          className={cn(
            "flex items-center justify-between w-full h-[40px] rounded-md border border-greyscale-border bg-white px-sm text-sm transition-colors",
            "hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-brand-primary/40",
            !selected && "text-greyscale-text-caption",
            className
          )}
        >
          <span className="truncate text-left">
            {selected ? (
              <>
                <span className="text-greyscale-text-title">
                  {selected.label}
                </span>
                {selected.sublabel && (
                  <span className="text-greyscale-text-caption">
                    {" "}
                    — {selected.sublabel}
                  </span>
                )}
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown
            size={16}
            className="text-greyscale-text-caption shrink-0 ml-xs"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        onKeyDown={handleKeyDown}
      >
        <div className="p-xs border-b border-greyscale-border">
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery("")}
            placeholder="Search…"
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            autoFocus
          />
        </div>
        <div
          id={listboxId}
          role="listbox"
          className="max-h-[260px] overflow-y-auto py-xs"
        >
          {filtered.length === 0 ? (
            <p className="px-md py-sm text-sm text-greyscale-text-caption">
              {emptyMessage}
            </p>
          ) : (
            filtered.map((opt, index) => {
              const isSelected = opt.value === value;
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={opt.value}
                  id={`${optionIdBase}-${index}`}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => commitSelection(index)}
                  className={cn(
                    "w-full flex items-center gap-xs px-md py-xs text-left text-sm transition-colors",
                    isHighlighted && "bg-neutral-100",
                    isSelected &&
                      "bg-secondary-pink-50 text-greyscale-text-title",
                    !isHighlighted && !isSelected && "text-greyscale-text-body"
                  )}
                >
                  <Check
                    size={14}
                    className={cn(
                      "shrink-0 text-primary-brand-primary",
                      isSelected ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex-1 min-w-0 truncate">
                    <span className="font-semibold">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-greyscale-text-caption font-regular">
                        {" "}
                        — {opt.sublabel}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
